import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { MenuItems } from '../enums/MenuItems'
import tenantApi from '../api/tenant/TenantApi'
import { AdminTenant } from '../api/tenant/models/adminGet/AdminGetRes'
import UserRolePill from '../components/UserRolePill'
import ToggleSwitch from '../components/ToggleSwitch'
import { UserDto } from '../api/tenant/models/UserDto'
import { useSafeUserData } from '../stateStores/User'
import { Pages } from '../Pages'
import PersonUi from '../components/PersonUi'
import SearchPeopleModal from '../components/SearchPeopleModal'
import { PersonDto } from '../api/person/models/PersonDto'
import SearchCompaniesModal from '../components/SearchCompaniesModal'
import { CompanyDto } from '../api/company/models/CompanyDto'
import LayoutSingleView from '../components/layout/LayoutSingleView'
import Section from '../components/Section'

function ManageTenant(): React.JSX.Element {
    const { user: myUser } = useSafeUserData()
    const [searchParams] = useSearchParams()
    const [tenant, setTenant] = useState<AdminTenant>()
    // const [isModalOpen, setIsModalOpen] = useState(false)
    // const [isPresentModalOpen, setIsPresentModalOpen] = useState(false)
    const tenantId = searchParams.get('id')
    const navigate = useNavigate()
    const [isSearchPersonModalOpen, setIsSearchPersonModalOpen] =
        useState(false)
    const [isSearchCompanyModalOpen, setIsSearchCompanyModalOpen] =
        useState(false)

    const refreshTenant = useCallback(async () => {
        if (!tenantId) {
            return
        }

        const tenant = (await tenantApi.adminGet(tenantId)).tenant
        setTenant(tenant)
        console.log('tenant', tenant.companies)
    }, [tenantId])

    useEffect(() => {
        refreshTenant()
    }, [tenantId, refreshTenant])

    async function handleSelectPerson(person: PersonDto) {
        await tenantApi.addNetworkSeedPerson(person.id, tenantId!)

        await refreshTenant()
        setIsSearchPersonModalOpen(false)
    }

    async function handleSelectCompany(companies: CompanyDto[]) {
        const ids = companies.map((company) => company.id)
        await tenantApi.linkTenantToCompanies(ids, tenantId!)

        await refreshTenant()
        setIsSearchCompanyModalOpen(false)
    }

    async function handleRemovePerson(person: PersonDto) {
        await tenantApi.removeNetworkSeedPerson(person.id, tenantId!)

        await refreshTenant()
        setIsSearchPersonModalOpen(false)
    }

    async function handleUserActiveChanged(user: UserDto) {
        if (myUser.id === user.id) {
            alert('You cannot deactivate your own user')
        } else {
            alert('TODO')
        }
    }

    async function handleRemoveCompany(companyId: string) {
        if (!tenant) return
        await tenantApi.unlinkCompanyFromTenant(companyId, tenant.id)

        await refreshTenant()
    }

    return (
        <LayoutSingleView activeMenuItem={MenuItems.Tenants}>
            <div>
                <h1 className="page-title">Manage Tenant</h1>
                <h2 className="page-subtitle">Tenant Details</h2>

                <Section title="Name" titleType="h2">
                    <div>{tenant?.name}</div>
                </Section>

                <Section
                    title="Connected Companies"
                    titleType="h2"
                    className="mt-2"
                >
                    {tenant?.companies ? (
                        <div className="mt-2 flex flex-wrap gap-2">
                            {tenant?.companies?.map((company) => (
                                <span
                                    key={company.id}
                                    className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full flex items-center gap-2"
                                >
                                    {company.name}
                                    <button
                                        onClick={() =>
                                            handleRemoveCompany(company.id)
                                        }
                                        className="text-blue-600 hover:text-red-500 ml-1"
                                        title="Remove company"
                                    >
                                        &times;
                                    </button>
                                </span>
                            ))}
                        </div>
                    ) : (
                        <div className="italic">No company connected</div>
                    )}

                    <button
                        className="primary-button my-4"
                        onClick={() => {
                            setIsSearchCompanyModalOpen(true)
                        }}
                    >
                        Connect Companies
                    </button>
                </Section>
                <div className="section">
                    <h2>People Available as Network Links</h2>
                    <button
                        className="primary-button mb-4"
                        onClick={() => {
                            setIsSearchPersonModalOpen(true)
                        }}
                    >
                        Add Person
                    </button>
                    <div className="flex flex-wrap gap-4">
                        {tenant?.availableAsSeedPersons?.map((person) => (
                            <div
                                className="flex flex-col"
                                key={`seed-person-${person.id}`}
                            >
                                <PersonUi person={person} />
                                <button
                                    className="remove-button"
                                    onClick={() => {
                                        handleRemovePerson(person)
                                    }}
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="section">
                    <h2>Users</h2>
                    <button
                        className="primary-button mb-4"
                        onClick={() => {
                            navigate(
                                `${Pages.CreateNewUser}?tenantId=${tenantId}`,
                            )
                        }}
                    >
                        Add User
                    </button>
                    <table>
                        <thead>
                            <tr>
                                <th>Id</th>
                                <th>Email</th>
                                <th>Active</th>
                                <th>Roles</th>
                                <th>Linked Person</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tenant?.users.map((user) => (
                                <tr key={user.id}>
                                    <td>{user.id}</td>
                                    <td>
                                        <Link
                                            to={`${Pages.UpdateUser}?userId=${user.id}`}
                                        >
                                            {user.email}
                                        </Link>
                                    </td>
                                    <td>
                                        <ToggleSwitch
                                            checked={user.isActive}
                                            onChange={() => {
                                                handleUserActiveChanged(user)
                                            }}
                                        />
                                    </td>
                                    <td>
                                        {user.userRoles.map((role) => (
                                            <UserRolePill
                                                key={`${user.id}-user-role-${role}`}
                                                value={role}
                                            />
                                        ))}
                                    </td>
                                    <td>
                                        {user.person && (
                                            <PersonUi person={user.person} />
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {/* <div className="section">
                    <h2 className="page-subtitle">Interviewing Team</h2>
                    <button
                        className="secondary-btn"
                        onClick={() => setIsModalOpen(true)}
                    >
                        Add new interviewing team member
                    </button>
                    {role && role.hiringTeam!.length > 0 && (
                        <table className="main-table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {role.hiringTeam!.map((person) => (
                                    <tr key={person.id}>
                                        <td>
                                            <PersonUi person={person} />
                                            <button
                                                className="remove-button"
                                                onClick={() =>
                                                    handleRemovePerson(
                                                        person.id,
                                                    )
                                                }
                                            >
                                                Remove
                                            </button>
                                        </td>
                                        <td>
                                            <table className="nested-table interview-team-work-history-table">
                                                <colgroup>
                                                    <col
                                                        style={{ width: '40%' }}
                                                    />
                                                    <col
                                                        style={{ width: '40%' }}
                                                    />
                                                    <col
                                                        style={{ width: '10%' }}
                                                    />
                                                    <col
                                                        style={{ width: '10%' }}
                                                    />
                                                </colgroup>
                                                <thead>
                                                    <tr>
                                                        <th>Company Name</th>
                                                        <th>Job Title</th>
                                                        <th>Start Date</th>
                                                        <th>End Date</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {person
                                                        .workedAts!.slice()
                                                        .sort((a, b) => {
                                                            if (!a.startDate)
                                                                return -1
                                                            if (!b.startDate)
                                                                return 1
                                                            return (
                                                                new Date(
                                                                    b.startDate,
                                                                ).getTime() -
                                                                new Date(
                                                                    a.startDate,
                                                                ).getTime()
                                                            )
                                                        })
                                                        .map((work, index) => (
                                                            <tr key={index}>
                                                                <td className="company-name">
                                                                    {
                                                                        work.company!
                                                                            .name
                                                                    }
                                                                </td>
                                                                <td className="job-title">
                                                                    {
                                                                        work.jobTitle
                                                                    }
                                                                </td>
                                                                <td className="start-date">
                                                                    {formatDate(
                                                                        work.startDate,
                                                                    )}
                                                                </td>
                                                                <td className="end-date">
                                                                    {formatDate(
                                                                        work.endDate,
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div> */}
                {/* {isModalOpen && (
                    <SearchPeopleModal
                        onClose={() => setIsModalOpen(false)}
                        onSelect={handleSelectPerson}
                    />
                )}
                {isPresentModalOpen && (
                    <SelectPersonModal
                        persons={role!.hiringTeam!.map((value) => {
                            return value
                        })}
                        onClose={() => setIsPresentModalOpen(false)}
                        onSelect={handleSelectPersonToPresent}
                    />
                )} */}
                {isSearchPersonModalOpen && (
                    <SearchPeopleModal
                        onClose={() => setIsSearchPersonModalOpen(false)}
                        onSelect={handleSelectPerson}
                    />
                )}
                {isSearchCompanyModalOpen && (
                    <SearchCompaniesModal
                        onClose={() => setIsSearchCompanyModalOpen(false)}
                        onSelect={handleSelectCompany}
                    />
                )}
            </div>
        </LayoutSingleView>
    )
}

export default ManageTenant
